import { Send, Terminal, TriangleAlert } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Form } from '@nucleus-commerce/ui/components/form';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { PAYMENT_OPERATIONS, takesIdempotencyKey, toCurl } from '../../api/try-it-client';
import type { DocsOperation } from '../../generated/reference';
import { useDocsSession } from '../../hooks/use-docs-session';
import { useTryIt } from '../../hooks/use-try-it';
import {
  exampleBody,
  formParams,
  tryItDefaults,
  tryItFormSchema,
  type TryItValues,
} from '../../schemas/try-it-form-schema';

import { BodyEditor } from './body-editor';
import { copyText } from './copy-text';
import { CustomerTokenChip } from './customer-token-chip';
import { IdempotencyField } from './idempotency-field';
import { ParamField } from './param-field';
import { PaymentConfirmDialog } from './payment-confirm-dialog';
import { ResponseView } from './response-view';

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="text-muted-foreground mb-2 text-xs font-medium tracking-wide uppercase">
        {title}
      </legend>
      {children}
    </fieldset>
  );
}

/**
 * The request builder for one operation (plan 9.2). Mount it with `key={operation.id}`: starting
 * values (remembered path params, the recorded body, a fresh Idempotency-Key) are read once.
 */
export function TryItForm({ operation }: { operation: DocsOperation }) {
  const { t } = useTranslation('docs');
  const docsSession = useDocsSession();
  const tryIt = useTryIt(operation);
  const [initial] = useState(() => tryItDefaults(operation, docsSession));
  const schema = useMemo(() => tryItFormSchema(operation), [operation]);
  const form = useForm<TryItValues>({
    resolver: useI18nResolver(schema),
    defaultValues: initial.values,
  });
  const [reuseLast, setReuseLast] = useState(false);
  const [pending, setPending] = useState<TryItValues | null>(null);

  const idempotent = takesIdempotencyKey(operation);
  const pathParams = formParams(operation, 'path');
  const queryParams = formParams(operation, 'query');
  const headerParams = formParams(operation, 'header');

  const dispatch = (values: TryItValues) => {
    tryIt.send(values);
    if (idempotent && !reuseLast) form.setValue('idempotencyKey', crypto.randomUUID());
  };

  const submit = form.handleSubmit((values) => {
    if (PAYMENT_OPERATIONS.has(operation.id) && !docsSession.paymentConfirmed) {
      setPending(values);
      return;
    }
    dispatch(values);
  });

  const copyCurl = async () => {
    if (!(await form.trigger())) return;
    const request = tryIt.buildRequest(form.getValues());
    if (request) await copyText(toCurl(request), t);
  };

  const onReuseLast = (reuse: boolean) => {
    setReuseLast(reuse);
    const last = docsSession.idempotency.last;
    form.setValue('idempotencyKey', reuse && last ? last : crypto.randomUUID());
  };

  return (
    <Form {...form}>
      <form onSubmit={(event) => void submit(event)} noValidate className="flex flex-col gap-5">
        {pathParams.length > 0 ? (
          <Group title={t('tryIt.pathParams')}>
            {pathParams.map((param) => (
              <ParamField
                key={param.name}
                control={form.control}
                param={param}
                source={initial.sources[param.name]}
                suggestions={docsSession.remembered[param.name]}
              />
            ))}
          </Group>
        ) : null}

        {queryParams.length > 0 ? (
          <Group title={t('tryIt.queryParams')}>
            {queryParams.map((param) => (
              <ParamField key={param.name} control={form.control} param={param} />
            ))}
          </Group>
        ) : null}

        {headerParams.length > 0 ||
        idempotent ||
        operation.customerToken ||
        docsSession.customer ? (
          <Group title={t('tryIt.headers')}>
            <CustomerTokenChip
              customer={docsSession.customer}
              required={operation.customerToken}
              onClear={docsSession.clearCustomer}
            />
            {headerParams.map((param) => (
              <ParamField key={param.name} control={form.control} param={param} />
            ))}
            {idempotent ? (
              <IdempotencyField
                control={form.control}
                reuseLast={reuseLast}
                canReuse={docsSession.idempotency.last !== null}
                onReuseLastChange={onReuseLast}
                onRegenerate={() => form.setValue('idempotencyKey', crypto.randomUUID())}
              />
            ) : null}
          </Group>
        ) : null}

        {operation.requestBody ? (
          <BodyEditor
            control={form.control}
            onReset={() =>
              form.setValue('body', exampleBody(operation, docsSession), { shouldValidate: true })
            }
          />
        ) : null}

        {operation.mutates ? (
          <Alert>
            <TriangleAlert aria-hidden />
            <AlertDescription>{t('tryIt.mutates')}</AlertDescription>
          </Alert>
        ) : null}

        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Button type="submit" className="flex-1" disabled={!tryIt.canSend || tryIt.isPending}>
              <Send aria-hidden />
              {tryIt.isPending ? t('tryIt.sending') : t('tryIt.send')}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={!tryIt.canSend}
              onClick={() => void copyCurl()}
            >
              <Terminal aria-hidden />
              {t('tryIt.copyCurl')}
            </Button>
          </div>
          {tryIt.canSend ? null : (
            <p className="text-muted-foreground text-xs">{t('tryIt.needsKey')}</p>
          )}
        </div>

        <ResponseView response={tryIt.response} failed={tryIt.error !== null} />
      </form>

      <PaymentConfirmDialog
        open={pending !== null}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          docsSession.confirmPayments();
          if (pending) dispatch(pending);
          setPending(null);
        }}
      />
    </Form>
  );
}
